import { Component, OnDestroy, OnInit } from '@angular/core';
import { gql } from 'apollo-angular';
import { unparse } from 'papaparse';
import { Subject, Subscription, EMPTY, from, of } from 'rxjs';
import { catchError, debounceTime, switchMap, take, tap, mergeMap } from 'rxjs/operators';

import { PlusApollo } from '../../core/auth/graphql.module';
import { dayjs } from '../../core/dayjs/dayjs';
import { GraphQLModule } from '../../core/public-api';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { DashboardStore } from '../dashboardv2.store';

const SELF_IMPROVING_AI_JOBS_QUERY = gql`
  query SelfImprovingAIJobs($zone: String, $start: Int, $end: Int) {
    selfImprovingAIJobs(zone: $zone, start: $start, end: $end) {
      jobs {
        id
        code
        stage
        state
        total
        currency
        closedAt
        createdAt
        customerName
        closedReason
        closedBy
        siaAnalysis
      }
      total
      hasMore
    }
  }
`;

const RUN_SELF_IMPROVING_ANALYSIS_MUTATION = gql`
  mutation RunSelfImprovingAnalysis($jobId: String!, $zone: String, $theme: String) {
    runSelfImprovingAnalysis(jobId: $jobId, zone: $zone, theme: $theme) {
      jobId
      jobCode
      summary
      good
      issues
      suggestions
    }
  }
`;

const SUGGEST_IMPROVEMENTS_MUTATION = gql`
  mutation SuggestImprovements($analyses: [AnalysisSummaryInput!]!, $zone: String, $theme: String) {
    suggestImprovements(analyses: $analyses, zone: $zone, theme: $theme) {
      overallSummary
      themeFeedback
      pageDiffs {
        path
        title
        currentMarkdown
        proposedMarkdown
        changeSummary
        pageId
        zoneId
        isNewPage
      }
      memoryUpdated
      changelog
    }
  }
`;

const APPLY_PAGE_DIFF_MUTATION = gql`
  mutation ApplyPageDiff($pageId: String!, $markdown: String!) {
    editPage(pageId: $pageId, pageEdit: { markdown: $markdown }) {
      pageEdited
    }
  }
`;

const CREATE_PAGE_MUTATION = gql`
  mutation CreateSelfImprovingPage($title: String!, $path: String!, $markdown: String!, $zones: [String!]) {
    createPage(page: { title: $title, path: $path, markdown: $markdown, zones: $zones }) {
      id
    }
  }
`;

const LOG_APPLIED_CHANGES_MUTATION = gql`
  mutation LogAppliedChanges($zone: String, $changes: [AppliedChangeInput!]!) {
    logAppliedChanges(zone: $zone, changes: $changes)
  }
`;

const SIA_PAGE_TREE_QUERY = gql`
  query SiaPageTree($zone: String) {
    siaPageTree(zone: $zone) {
      path
      title
      children {
        path
        title
        children {
          path
          title
          children {
            path
            title
            children {
              path
              title
              children {
                path
                title
                children {
                  path
                  title
                  children {
                    path
                    title
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

interface ClosedJob {
	id: string;
	code: string;
	stage: string;
	state: string;
	total: number;
	currency: string;
	closedAt: number;
	createdAt: number;
	customerName: string;
	closedReason: string;
	closedBy: string;
	siaAnalysis: string;
	analysis?: JobAnalysis;
	analyzing?: boolean;
}

interface PageDiff {
	path: string;
	title: string;
	currentMarkdown: string;
	proposedMarkdown: string;
	changeSummary: string;
	pageId: string;
	zoneId: string;
	isNewPage: boolean;
	applied?: boolean;
	computedLines?: DiffLine[];
}

interface JobAnalysis {
	jobId: string;
	jobCode: string;
	summary: string;
	good: string[];
	issues: string[];
	suggestions: string[];
}

interface DayGroup {
	dateKey: string;
	label: string;
	jobs: ClosedJob[];
	analyzing: boolean;
	analyzed: boolean;
}

const CONCURRENT_ANALYSES = 5;

@Component({
	selector: 'app-dashboard-self-improving-ai',
	standalone: false,
	templateUrl: './dashboard-self-improving-ai.component.html',
	styleUrls: [
		'../dashboardv2.component.scss',
		'./dashboard-self-improving-ai.component.scss',
	],
})
export class DashboardSelfImprovingAIComponent implements OnInit, OnDestroy {

	jobs: ClosedJob[] = [];
	dayGroups: DayGroup[] = [];
	totalJobs = 0;
	hasMore = false;
	loading = false;
	runningAll = false;
	suggestingImprovements = false;
	memoryUpdated = false;
	changelog = '';
	overallSummary = '';
	themeFeedback: string | null = null;
	lastReload = 0;
	theme = '';
	previousTheme = '';
	expandedDays = new Set<string>();
	noChangesSuggested = false;
	summaryPageDiffs: PageDiff[] = [];
	pageTree: PageTreeNode[] = [];
	expandedPaths = new Set<string>();

	private reload$ = new Subject<void>();
	private theme$ = new Subject<string>();
	private subs: Subscription[] = [];

	constructor(
		private dashboardStore: DashboardStore,
		private apollo: PlusApollo,
		private graphqlModule: GraphQLModule,
		private notify: FreyaNotificationsService,
	) {}

	ngOnInit(): void {
		this.subs.push(
			this.dashboardStore.unixPeriod$.subscribe(() => {
				this.reload$.next();
			}),
		);

		this.subs.push(
			this.theme$.pipe(
				debounceTime(400),
			).subscribe((theme) => {
				const trimmed = (theme || '').trim();
				const prev = (this.previousTheme || '').trim();
				if (trimmed !== prev) {
					this.previousTheme = theme;
					if (trimmed === '') {
						this.restoreDefaultAnalysis();
					} else {
						this.clearAnalysis();
					}
				}
			}),
		);

		this.subs.push(
			this.reload$.pipe(
				debounceTime(100),
				switchMap(() => {
					const zone = this.graphqlModule.zone.value;
					if (!zone) {
						this.loading = false;
						return EMPTY;
					}

					this.loading = true;

					return this.dashboardStore.unixPeriod$.pipe(
						take(1),
						switchMap(({ start, end }) => {
							return this.apollo.query({
								query: SELF_IMPROVING_AI_JOBS_QUERY,
								variables: { zone, start, end },
							});
						}),
						tap(({ data }: any) => {
							const result = data?.selfImprovingAIJobs;
							this.jobs = (result?.jobs ?? []).map((j: ClosedJob) => {
								let analysis: JobAnalysis | undefined;
								if (j.siaAnalysis) {
									try {
										analysis = { ...JSON.parse(j.siaAnalysis), jobId: j.id, jobCode: j.code };
									} catch { /* ignore malformed */ }
								}
								return { ...j, analysis, analyzing: false };
							});
							this.totalJobs = result?.total ?? 0;
							this.hasMore = result?.hasMore ?? false;
							this.buildDayGroups();
							this.loading = false;
							this.lastReload = Math.floor(Date.now() / 1000);
						}),
						catchError(() => {
							this.loading = false;
							this.lastReload = Math.floor(Date.now() / 1000);
							return EMPTY;
						}),
					);
				}),
			).subscribe(),
		);

		this.reload$.next();
		this.loadPageTree();
	}

	ngOnDestroy(): void {
		this.subs.forEach((s) => s.unsubscribe());
		this.reload$.complete();
		this.theme$.complete();
	}

	loadPageTree(): void {
		const zone = this.graphqlModule.zone.value;
		if (!zone) { return; }

		this.apollo.query({
			query: SIA_PAGE_TREE_QUERY,
			variables: { zone },
		}).subscribe({
			next: ({ data }: any) => {
				this.pageTree = data?.siaPageTree || [];
				this.expandedPaths = new Set<string>();
				for (const root of this.pageTree) {
					this.expandedPaths.add(root.path);
				}
			},
			error: () => {
				this.pageTree = [];
			},
		});
	}

	togglePath(path: string): void {
		if (this.expandedPaths.has(path)) {
			this.expandedPaths.delete(path);
		} else {
			this.expandedPaths.add(path);
		}
	}

	isPathExpanded(path: string): boolean {
		return this.expandedPaths.has(path);
	}

	buildDayGroups(): void {
		const groups = new Map<string, ClosedJob[]>();
		for (const job of this.jobs) {
			const dateKey = dayjs.unix(job.closedAt).format('YYYY-MM-DD');
			const dayJobs = groups.get(dateKey) || [];
			dayJobs.push(job);
			groups.set(dateKey, dayJobs);
		}

		this.dayGroups = Array.from(groups.entries())
			.sort(([a], [b]) => a.localeCompare(b))
			.map(([dateKey, dayJobs]) => ({
				dateKey,
				label: dayjs(dateKey).format('ddd, MMM D'),
				jobs: [...dayJobs].sort((a, b) => a.closedAt - b.closedAt),
				analyzing: dayJobs.some((j) => j.analyzing),
				analyzed: dayJobs.every((j) => !!j.analysis),
			}));
	}

	toggleDayExpanded(dateKey: string): void {
		if (this.expandedDays.has(dateKey)) {
			this.expandedDays.delete(dateKey);
		} else {
			this.expandedDays.add(dateKey);
		}
	}

	isDayExpanded(dateKey: string): boolean {
		return this.expandedDays.has(dateKey);
	}

	isAnalyzing(): boolean {
		return this.runningAll || this.jobs.some((j) => j.analyzing);
	}

	isDayAnalyzing(group: DayGroup): boolean {
		return group.jobs.some((j) => j.analyzing);
	}

	isDayAnalyzed(group: DayGroup): boolean {
		return group.jobs.length > 0 && group.jobs.every((j) => !!j.analysis);
	}

	hasAnalyzedJobs(): boolean {
		return this.jobs.some((j) => !!j.analysis);
	}

	analyzedCount(): number {
		return this.jobs.filter((j) => !!j.analysis).length;
	}

	allAnalyzed(): boolean {
		return this.jobs.length > 0 && this.jobs.every((j) => !!j.analysis);
	}

	onThemeChange(): void {
		this.theme$.next(this.theme);
	}

	clearAnalysis(): void {
		for (const job of this.jobs) {
			job.analysis = undefined;
			job.analyzing = false;
		}
		this.summaryPageDiffs = [];
		this.overallSummary = '';
		this.themeFeedback = null;
		this.memoryUpdated = false;
		this.noChangesSuggested = false;
		this.changelog = '';
		this.buildDayGroups();
	}

	restoreDefaultAnalysis(): void {
		for (const job of this.jobs) {
			job.analyzing = false;
			if (job.siaAnalysis) {
				try {
					job.analysis = { ...JSON.parse(job.siaAnalysis), jobId: job.id, jobCode: job.code };
				} catch {
					job.analysis = undefined;
				}
			} else {
				job.analysis = undefined;
			}
		}
		this.summaryPageDiffs = [];
		this.overallSummary = '';
		this.themeFeedback = null;
		this.memoryUpdated = false;
		this.noChangesSuggested = false;
		this.changelog = '';
		this.buildDayGroups();
	}

	runAnalysis(jobId: string): void {
		if (this.isAnalyzing()) { return; }
		const zone = this.graphqlModule.zone.value;
		const job = this.jobs.find((j) => j.id === jobId);
		if (job) {
			job.analyzing = true;
			job.analysis = undefined;
		}

		this.apollo.mutate({
			mutation: RUN_SELF_IMPROVING_ANALYSIS_MUTATION,
			variables: { jobId, zone, theme: this.theme || null },
		}).subscribe({
			next: ({ data }: any) => {
				const result = data?.runSelfImprovingAnalysis;
				if (result && job) {
					job.analysis = result;
					job.analyzing = false;
					this.buildDayGroups();
				}
				this.notify.success('Analysis Complete', `Job ${result?.jobCode || jobId} analyzed.`);
			},
			error: (err) => {
				if (job) {
					job.analyzing = false;
				}
				this.buildDayGroups();
				this.notify.error('Analysis Failed', err.message || 'An error occurred during analysis.');
			},
		});
	}

	runAnalysisDay(group: DayGroup): void {
		if (this.isAnalyzing()) { return; }
		const targetJobs = group.jobs.filter((j) => !j.analyzing);
		if (targetJobs.length === 0) { return; }

		group.analyzing = true;
		const zone = this.graphqlModule.zone.value;

		from(targetJobs).pipe(
			mergeMap((job) => {
				job.analyzing = true;
				job.analysis = undefined;
				return this.apollo.mutate({
					mutation: RUN_SELF_IMPROVING_ANALYSIS_MUTATION,
					variables: { jobId: job.id, zone, theme: this.theme || null },
				}).pipe(
					tap(({ data }: any) => {
						const result = data?.runSelfImprovingAnalysis;
						if (result) {
							job.analysis = result;
							job.analyzing = false;
						}
					}),
					catchError((err) => {
						job.analyzing = false;
						this.notify.error('Analysis Failed', `Job ${job.code}: ${err.message}`);
						return of(null);
					}),
				);
			}, CONCURRENT_ANALYSES),
		).subscribe({
			complete: () => {
				group.analyzing = false;
				this.buildDayGroups();
				const analyzed = targetJobs.filter((j) => j.analysis).length;
				this.notify.success('Day Analysis Complete', `Analyzed ${analyzed} of ${targetJobs.length} jobs for ${group.label}.`);
			},
		});
	}

	runAnalysisAll(): void {
		if (this.runningAll) { return; }
		const targetGroups = this.dayGroups
			.map((g) => ({ ...g, jobs: g.jobs.filter((j) => !j.analyzing) }))
			.filter((g) => g.jobs.length > 0);

		if (targetGroups.length === 0) { return; }

		this.runningAll = true;
		const zone = this.graphqlModule.zone.value;

		for (const group of targetGroups) {
			for (const job of group.jobs) {
				job.analyzing = true;
				job.analysis = undefined;
			}
		}

		from(targetGroups).pipe(
			mergeMap((group) => {
				group.analyzing = true;
				return from(group.jobs).pipe(
					mergeMap((job) => {
						return this.apollo.mutate({
							mutation: RUN_SELF_IMPROVING_ANALYSIS_MUTATION,
							variables: { jobId: job.id, zone, theme: this.theme || null },
						}).pipe(
							tap(({ data }: any) => {
								const result = data?.runSelfImprovingAnalysis;
								if (result) {
									job.analysis = result;
									job.analyzing = false;
								}
							}),
							catchError((err) => {
								job.analyzing = false;
								this.notify.error('Analysis Failed', `Job ${job.code}: ${err.message}`);
								return of(null);
							}),
						);
					}, CONCURRENT_ANALYSES),
				);
			}, 1),
		).subscribe({
			complete: () => {
				this.runningAll = false;
				this.buildDayGroups();
				const totalAnalyzed = targetGroups.reduce(
					(sum, g) => sum + g.jobs.filter((j) => j.analysis).length, 0,
				);
				const totalJobs = targetGroups.reduce((sum, g) => sum + g.jobs.length, 0);
				this.notify.success(
					'All Analyses Complete',
					`Analyzed ${totalAnalyzed} of ${totalJobs} jobs across ${targetGroups.length} day(s).`,
				);
			},
		});
	}

	suggestImprovements(): void {
		const analyzedJobs = this.jobs.filter((j) => j.analysis);
		if (analyzedJobs.length === 0) { return; }

		this.suggestingImprovements = true;
		this.noChangesSuggested = false;
		const zone = this.graphqlModule.zone.value;

		const analyses = analyzedJobs.map((j) => ({
			jobId: j.analysis!.jobId,
			jobCode: j.analysis!.jobCode,
			summary: j.analysis!.summary,
			good: j.analysis!.good,
			issues: j.analysis!.issues,
			suggestions: j.analysis!.suggestions,
		}));

		this.apollo.mutate({
			mutation: SUGGEST_IMPROVEMENTS_MUTATION,
			variables: { analyses, zone, theme: this.theme || null },
		}).subscribe({
			next: ({ data }: any) => {
				const result = data?.suggestImprovements;
				const rawDiffs: PageDiff[] = (result?.pageDiffs || []).map((d: PageDiff) => ({ ...d, applied: false }));
				const meaningfulDiffs = rawDiffs.filter((d) => {
					if (d.isNewPage || !d.currentMarkdown) {
						return true;
					}
					const currentTrimmed = (d.currentMarkdown || '').trim();
					const proposedTrimmed = (d.proposedMarkdown || '').trim();
					return currentTrimmed !== proposedTrimmed;
				}).map((d) => ({
					...d,
					computedLines: this.computeDiff(d.currentMarkdown, d.proposedMarkdown),
				}));
				this.summaryPageDiffs = meaningfulDiffs;
				this.noChangesSuggested = meaningfulDiffs.length === 0;
				this.overallSummary = result?.overallSummary || '';
				this.themeFeedback = result?.themeFeedback || null;
				this.memoryUpdated = result?.memoryUpdated ?? false;
				this.changelog = result?.changelog || '';
				this.suggestingImprovements = false;
				const memoryNote = this.memoryUpdated ? ' Memory page updated.' : '';
				this.notify.success(
					'Improvements Suggested',
					`Generated ${meaningfulDiffs.length} page improvement(s) from ${analyzedJobs.length} job(s).${memoryNote}`,
				);
			},
			error: (err) => {
				this.suggestingImprovements = false;
				this.notify.error('Suggestion Failed', err.message || 'Failed to generate suggestions.');
			},
		});
	}

	applyPageDiff(diff: PageDiff): void {
		const zone = this.graphqlModule.zone.value;

		const mutation = diff.isNewPage || !diff.pageId
			? this.apollo.mutate({
				mutation: CREATE_PAGE_MUTATION,
				variables: {
					title: diff.title,
					path: diff.path,
					markdown: diff.proposedMarkdown,
					zones: [zone],
				},
			})
			: this.apollo.mutate({
				mutation: APPLY_PAGE_DIFF_MUTATION,
				variables: {
					pageId: diff.pageId,
					markdown: diff.proposedMarkdown,
				},
			});

		mutation.subscribe({
			next: ({ data }: any) => {
				diff.applied = true;
				if ((diff.isNewPage || !diff.pageId) && data?.createPage?.id) {
					diff.pageId = data.createPage.id;
				}
				this.summaryPageDiffs = [...this.summaryPageDiffs];
				this.logAppliedChanges([diff]);
				this.notify.success('Page Updated', `Applied changes to ${diff.title}`);
			},
			error: (err) => {
				this.notify.error('Update Failed', err.message || 'Failed to apply page changes.');
			},
		});
	}

	applyAllPageDiffs(): void {
		const toApply = this.summaryPageDiffs.filter((d) => !d.applied);
		for (const diff of toApply) {
			this.applyPageDiff(diff);
		}
	}

	allPageDiffsApplied(): boolean {
		return this.summaryPageDiffs.length > 0 && this.summaryPageDiffs.every((d) => d.applied);
	}

	hasAnyDiffs(): boolean {
		return this.summaryPageDiffs.length > 0;
	}

	logAppliedChanges(diffs: PageDiff[]): void {
		const changes: AppliedChange[] = diffs.map((d) => ({
			path: d.path,
			title: d.title,
			changeSummary: d.changeSummary,
			isNewPage: d.isNewPage,
		}));
		const zone = this.graphqlModule.zone.value;
		this.apollo.mutate({
			mutation: LOG_APPLIED_CHANGES_MUTATION,
			variables: { zone, changes },
		}).subscribe({
			next: () => {},
			error: () => {},
		});
	}

	computeDiff(current: string, proposed: string): DiffLine[] {
		const currentLines = (current || '').split('\n');
		const proposedLines = (proposed || '').split('\n');
		const result: DiffLine[] = [];
		const maxLen = Math.max(currentLines.length, proposedLines.length);
		for (let i = 0; i < maxLen; i++) {
			const cLine = i < currentLines.length ? currentLines[i] : undefined;
			const pLine = i < proposedLines.length ? proposedLines[i] : undefined;
			if (cLine === pLine) {
				result.push({ type: 'context', content: cLine! });
			} else {
				if (cLine !== undefined) {
					result.push({ type: 'removed', content: cLine });
				}
				if (pLine !== undefined) {
					result.push({ type: 'added', content: pLine });
				}
			}
		}
		return result;
	}

	formatClosedTime(unix: number): string {
		return dayjs.unix(unix).format('h:mm A');
	}

	formatTotal(cents: number, currency: string): string {
		const amount = cents / 100;
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: currency || 'USD',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(amount);
	}

	private sanitizeCsvCell(value: unknown): string {
		const str = String(value ?? '');
		return /^[=+\-@]/.test(str) ? `'${str}` : str;
	}

	private buildHtmlTable(headers: string[], rows: string[][]): string {
		const thCells = headers.map((h) => `<th>${this.escapeHtml(h)}</th>`).join('');
		const trRows = rows.map((r) => `<tr>${r.map((c) => `<td>${this.escapeHtml(c)}</td>`).join('')}</tr>`).join('');
		return `<table><thead><tr>${thCells}</tr></thead><tbody>${trRows}</tbody></table>`;
	}

	private escapeHtml(value: string): string {
		return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	private copyToClipboard(text: string, html: string): void {
		const textBlob = new Blob([text], { type: 'text/plain' });
		const htmlBlob = new Blob([html], { type: 'text/html' });
		navigator.clipboard.write([
			new ClipboardItem({ 'text/plain': textBlob, 'text/html': htmlBlob }),
		]).catch(() => {
			navigator.clipboard.writeText(text).catch(() => {});
		});
	}

	downloadCSV(): void {
		const sorted = [...this.jobs].sort((a, b) => a.closedAt - b.closedAt);
		const headers = [
			'Code', 'Customer', 'Stage', 'State', 'Total', 'Currency',
			'ClosedAt', 'ClosedBy', 'ClosedReason', 'Summary', 'Good', 'Issues', 'Suggestions',
		];
		const rows = sorted.map((job) => [
			this.sanitizeCsvCell(job.code),
			this.sanitizeCsvCell(job.customerName),
			this.sanitizeCsvCell(job.stage),
			this.sanitizeCsvCell(job.state),
			this.sanitizeCsvCell(this.formatTotal(job.total, job.currency)),
			this.sanitizeCsvCell(job.currency),
			this.sanitizeCsvCell(dayjs.unix(job.closedAt).format('YYYY-MM-DD HH:mm')),
			this.sanitizeCsvCell(job.closedBy),
			this.sanitizeCsvCell(job.closedReason),
			this.sanitizeCsvCell(job.analysis?.summary || ''),
			this.sanitizeCsvCell((job.analysis?.good || []).join('; ')),
			this.sanitizeCsvCell((job.analysis?.issues || []).join('; ')),
			this.sanitizeCsvCell((job.analysis?.suggestions || []).join('; ')),
		]);

		const csv = unparse({ fields: headers, data: rows });
		const html = this.buildHtmlTable(headers, rows);
		this.copyToClipboard(csv, html);

		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `self-improving-ai-${dayjs().format('YYYY-MM-DD')}.csv`;
		link.click();
		URL.revokeObjectURL(url);
	}

	downloadAnalysisCSV(): void {
		const analyzed = this.jobs.filter((j) => j.analysis);
		if (analyzed.length === 0) { return; }

		const sorted = [...analyzed].sort((a, b) => a.closedAt - b.closedAt);
		const jobHeaders = ['Code', 'Customer', 'Total', 'ClosedAt', 'ClosedReason', 'Summary', 'Good', 'Issues', 'Suggestions'];
		const jobRows = sorted.map((job) => [
			this.sanitizeCsvCell(job.code),
			this.sanitizeCsvCell(job.customerName),
			this.sanitizeCsvCell(this.formatTotal(job.total, job.currency)),
			this.sanitizeCsvCell(dayjs.unix(job.closedAt).format('YYYY-MM-DD HH:mm')),
			this.sanitizeCsvCell(job.closedReason),
			this.sanitizeCsvCell(job.analysis?.summary || ''),
			this.sanitizeCsvCell((job.analysis?.good || []).join('; ')),
			this.sanitizeCsvCell((job.analysis?.issues || []).join('; ')),
			this.sanitizeCsvCell((job.analysis?.suggestions || []).join('; ')),
		]);

		const diffHeaders = ['Path', 'Title', 'ChangeSummary', 'IsNewPage', 'Applied'];
		const diffRows = this.summaryPageDiffs.map((d) => [
			this.sanitizeCsvCell(d.path),
			this.sanitizeCsvCell(d.title),
			this.sanitizeCsvCell(d.changeSummary),
			d.isNewPage ? 'Yes' : 'No',
			d.applied ? 'Yes' : 'No',
		]);

		const metadataRows = [
			{ Section: 'Focus Area', Value: this.sanitizeCsvCell(this.theme || '') },
			{ Section: 'Overall Summary', Value: this.sanitizeCsvCell(this.overallSummary || '') },
			{ Section: 'Theme Feedback', Value: this.sanitizeCsvCell(this.themeFeedback || '') },
		];
		const metadataCsv = unparse(metadataRows);
		const jobCsv = unparse({ fields: jobHeaders, data: jobRows });

		let csv = metadataCsv + '\n\n' + jobCsv;
		if (diffRows.length > 0) {
			csv += '\n\n' + unparse({ fields: diffHeaders, data: diffRows });
		}

		const html = this.buildHtmlTable(jobHeaders, jobRows);
		this.copyToClipboard(csv, html);

		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `sia-analysis-${dayjs().format('YYYY-MM-DD')}.csv`;
		link.click();
		URL.revokeObjectURL(url);
	}

	triggerReload(): void {
		this.dashboardStore.triggerReload();
		this.reload$.next();
	}
}

interface AppliedChange {
	path: string;
	title: string;
	changeSummary: string;
	isNewPage: boolean;
}

interface DiffLine {
	type: 'added' | 'removed' | 'context';
	content: string;
}

interface PageTreeNode {
	path: string;
	title: string;
	children: PageTreeNode[];
}